import React, { Component, type ReactNode, type ErrorInfo } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App'
import { AppReadyProvider } from './contexts/AppReadyContext'
import './index.css'
import { i18nReady } from './i18n/config'

// Dev helper to reset app state for testing - only in development
if (import.meta.env.DEV) {
  (window as unknown as { resetLukhasDemo: () => void }).resetLukhasDemo = () => {
    localStorage.removeItem('lukhas_language_selected')
    localStorage.removeItem('lukhas_language')
    localStorage.removeItem('lukhas_consent_v1')
    window.location.reload()
  }
}

/**
 * Top-level error boundary that catches chunk-load failures (stale cache)
 * and auto-reloads the page once to pick up fresh assets.
 */
class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const isChunkError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('error loading dynamically imported module')

    if (isChunkError) {
      const reloadKey = 'lukhas_chunk_reload'
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1')
        window.location.reload()
        return
      }
      sessionStorage.removeItem(reloadKey)
    }

    console.error('App error:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#030308',
          color: '#fff',
          fontFamily: '"Helvetica Neue", sans-serif',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400, padding: 24 }}>
            <p style={{ fontSize: 18, fontWeight: 300, marginBottom: 16 }}>
              Something went wrong loading LUKHΛS.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem('lukhas_chunk_reload')
                window.location.reload()
              }}
              style={{
                padding: '10px 24px',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'transparent',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Reload
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const root = ReactDOM.createRoot(document.getElementById('root')!)

void i18nReady
  .then(() => {
    root.render(
      <React.StrictMode>
        <ChunkErrorBoundary>
          <BrowserRouter>
            <HelmetProvider>
              <AppReadyProvider>
                <App />
              </AppReadyProvider>
            </HelmetProvider>
          </BrowserRouter>
        </ChunkErrorBoundary>
      </React.StrictMode>,
    )
  })
  .catch(() => {
    // i18n locale chunk failed to load (stale cache) — reload once
    const reloadKey = 'lukhas_i18n_reload'
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1')
      window.location.reload()
    } else {
      sessionStorage.removeItem(reloadKey)
      document.getElementById('root')!.innerHTML =
        '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#030308;color:#fff;font-family:sans-serif">' +
        '<div style="text-align:center"><p style="font-size:18px;font-weight:300;margin-bottom:16px">Failed to load LUKHΛS. Please refresh.</p>' +
        '<button onclick="sessionStorage.clear();location.reload()" style="padding:10px 24px;border-radius:12px;border:1px solid rgba(255,255,255,0.2);background:transparent;color:#fff;cursor:pointer">Reload</button></div></div>'
    }
  })
